/**
 * Sidebar collapse, persisted across reloads.
 *
 * The class lives on `<html>` rather than in React state alone, because
 * index.html re-applies it from localStorage BEFORE first paint. Without that,
 * a collapsed sidebar would flash open on every page load while React boots.
 * This hook keeps the two in step.
 */

import { useCallback, useEffect, useState } from 'react';

export const NAV_COLLAPSED_STORAGE_KEY = 'atlas.nav.collapsed';
const COLLAPSED_CLASS = 'nav-collapsed';

function readInitialState(): boolean {
  if (typeof document === 'undefined') return false;
  // The pre-paint script in index.html is the source of truth on first render,
  // so read the DOM rather than storage -- they agree, and this cannot fail.
  return document.documentElement.classList.contains(COLLAPSED_CLASS);
}

export function useNavCollapse() {
  const [collapsed, setCollapsed] = useState<boolean>(readInitialState);

  useEffect(() => {
    document.documentElement.classList.toggle(COLLAPSED_CLASS, collapsed);
    try {
      localStorage.setItem(NAV_COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // Private browsing or storage disabled. The toggle still works for this
      // session; it simply will not be remembered.
    }
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((current) => !current), []);

  return { collapsed, toggle };
}
